import React from "react";

/**
 * FramedArtwork — gallery-style frame + passe-partout that mimics a real
 * physical frame: dark wood border + white mat, uniform thickness around the
 * artwork. The image drives the aspect ratio — the mat and frame grow with it.
 *
 * Props:
 *   src         (string)  image url
 *   alt         (string)  alt text
 *   ratio       (number)  width / height of the original artwork. When provided,
 *                         the IMAGE takes this exact aspect (no crop, no letter-
 *                         box). Frame + mat wrap around it with equal padding.
 *   aspectClass (string)  Tailwind aspect-* fallback for when `ratio` is unknown
 *                         (default 'aspect-[4/5]'). Rarely needed.
 *   className   (string)  extra classes for the outer wrapper.
 *   hover       (boolean) apply hover lift (default true).
 *   imgTestId   (string)  data-testid for the inner <img>.
 */
export const FramedArtwork = ({
  src,
  alt = "",
  ratio,
  aspectClass = "aspect-[4/5]",
  className = "",
  hover = true,
  imgTestId,
}) => {
  const imageStyle = ratio && Number.isFinite(ratio) && ratio > 0
    ? { aspectRatio: `${ratio}` }
    : undefined;

  return (
    <div
      data-testid="framed-artwork"
      className={[
        // Outer visible dark frame (wood-grain feel)
        "relative bg-[#1a1410] p-[6px] sm:p-[8px] lg:p-[10px]",
        "shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45),0_10px_20px_-10px_rgba(0,0,0,0.25)]",
        hover
          ? "transition-transform duration-500 ease-out group-hover:-translate-y-1 group-hover:shadow-[0_40px_80px_-25px_rgba(0,0,0,0.55),0_14px_28px_-12px_rgba(0,0,0,0.3)]"
          : "",
        "before:content-[''] before:absolute before:inset-0 before:pointer-events-none",
        "before:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),inset_0_-2px_4px_rgba(0,0,0,0.4)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* White passe-partout (mat) — uniform gallery thickness */}
      <div
        className={[
          "relative bg-[#fbf9f4] p-[14px] sm:p-[18px] lg:p-[22px]",
          "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08),inset_0_4px_8px_-4px_rgba(0,0,0,0.08)]",
        ].join(" ")}
      >
        {/* Image window — takes the artwork's real aspect ratio */}
        <div
          className={imageStyle ? "relative w-full overflow-hidden shadow-[0_0_0_1px_rgba(0,0,0,0.12)] bg-[#fbf9f4]" : `${aspectClass} relative w-full overflow-hidden shadow-[0_0_0_1px_rgba(0,0,0,0.12)] bg-[#fbf9f4]`}
          style={imageStyle}
        >
          {src ? (
            <img
              src={src}
              alt={alt}
              loading="lazy"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              data-testid={imgTestId}
              className={[
                "block w-full h-full object-cover select-none",
                hover ? "transition-transform duration-700 ease-out group-hover:scale-[1.03]" : "",
              ].join(" ")}
              style={{ WebkitUserDrag: "none" }}
            />
          ) : (
            <div className="w-full h-full bg-neutral-200" />
          )}
        </div>
      </div>
    </div>
  );
};

export default FramedArtwork;
